import type { ScanAmazonOrder } from './components/AmazonOrdersStrip';
// Helpers + types compartidos entre la fachada WarehousePage y sus tabs/modals.

// Upgrade ML thumbnail URLs to higher resolution (replace -I.jpg with -O.jpg)
export const hiRes = (url: string | null | undefined): string | null => {
  if (!url) return null;
  return url.replace(/-I\.jpg$/i, '-O.jpg');
};

export interface BatchData {
  id: number;
  seller_id: number;
  seller_batch_num: number;
  batch_date: string;
  status: string;
  opened_at: string;
  closed_at: string | null;
  packing_release_url: string | null;
  seller: { id: number; nickname: string };
  counts: { total: number; received: number; packed: number; closed: number; alerts: number };
  recent_items: any[];
}

// ── Depósito con dos correos (2026-09-10) ──
export interface WhCourier {
  id: number;
  key: string;
  name: string;
  color: string;
  active: boolean;
  sort_order?: number;
}
export type WhMode = 'simple' | 'multi' | 'none';
export interface ActiveBagView {
  id: number;
  bag_code: string;
  opened_at: string;
  order_count: number;
  item_count: number;
  courier_id: number | null;
  courier: WhCourier | null;
}
/** Respuesta de GET /api/warehouse/bags/active */
export interface ActiveBagsView {
  bags: ActiveBagView[];
  mode: WhMode;
  couriers: WhCourier[];
  /** correos activos sin bolsa abierta (solo modo multi) */
  missing: WhCourier[];
  /** compat: primera bolsa abierta */
  bag: ActiveBagView | null;
}
export type BagGap = 'NO_BAG_FOR_COURIER' | 'COURIER_REQUIRED' | 'NO_ACTIVE_BAG' | null;

/**
 * La venta tal como la devuelve el escaneo. Se tipa solo lo que el pop-up usa de verdad.
 *
 * `pack_id` (de `orders.pack_id`, texto porque es un número muy largo) es OBLIGATORIO para hablar
 * con el comprador: la mensajería de Mercado Libre trabaja por PAQUETE, y pedirle la conversación
 * con el número de venta devuelve 400 "The order belong to a pack". Es `null` en las ventas
 * sueltas que de verdad no tienen paquete.
 */
export interface ScanOrderView {
  order_id?: number;
  ml_order_id: string | number;
  pack_id?: string | null;
  status: string;
  buyer_nickname: string | null;
  buyer_name?: string;
  date_created: string;
  [key: string]: unknown;
}

export interface ScanResult {
  type: string;
  purchase?: any;
  order?: ScanOrderView | null;
  shipment?: any;
  // ── Depósito con dos correos (2026-09-10): correo, bolsa destino y país ──
  courier?: WhCourier | null;
  courier_unknown?: boolean;
  target_bag?: { id: number; bag_code: string } | null;
  bag_gap?: BagGap;
  destination_site?: string | null;
  mode?: WhMode;
  flag_watermark?: boolean;
  couriers?: WhCourier[];
  claim?: any;
  items?: any[];
  pack?: any;
  seller?: any;
  internal_stock?: any;
  cross_seller?: boolean;
  already_processed?: any;
  already_associated?: boolean;
  shipment_items?: any[];
  matched_orders?: any[];
  /** Paquete ya procesado: otras ventas del mismo producto sin lote (más vieja primero) */
  pending_same_product?: any[];
  purchase_order?: any;
  code?: string;
  print_job?: {
    id: number;
    status: string;
    was_existing: boolean;
    printed_at?: string | null;
    agent_id?: string | null;
  };
  print_job_error?: string;
  order_label_id?: number;
  /**
   * Voltaje 110 V (spec §5.5): el PDF de esta orden salió SIN la tercera hoja
   * con el aviso "PRODUCTO DISEÑADO SOLO PARA 110V…" porque falló el render.
   * El pop-up lo muestra en rojo con un botón que regenera la etiqueta y la
   * vuelve a mandar a imprimir. Se apaga solo cuando la regeneración anda.
   */
  voltage_label_missing?: boolean;
  // Info-only fallback (orden cancelada / compra Amazon huérfana / tracking-only)
  sub_type?: 'amazon_purchase' | 'ml_shipment' | 'package_tracking';
  data?: any;
  // Devolución recibida por escaneo de su guía (type='ml_return', 2026-09-23):
  // datos de la devolución de ML (estado, dinero, guía, recepción sellada) e
  // historial cronológico de la venta para el pop-up ReturnScanModal.
  return?: any;
  timeline?: Array<{ at: string | null; kind: string; text: string }>;
  // Órdenes de Amazon ligadas a lo escaneado (2026-10-17): las muestra la franja de todos los pop-ups.
  amazon_orders?: ScanAmazonOrder[];
  // Paquete Amazon multi-producto (type='amazon_multi')
  package?: { tracking_number: string; carrier: string | null; amazon_order_ids: string[] };
  products?: MultiProduct[];
  pending_count?: number;
  processed_count?: number;
}

// Una tarjeta por compra Amazon dentro de un paquete multi-producto
export interface MultiProduct {
  purchase_id: number;
  asin: string;
  quantity: number;
  amazon_order_id: string | null;
  title: string;
  brand: string | null;
  thumbnail: string | null;
  order: {
    ml_order_id: string | number;
    /** Paquete de ML (texto). Ver `ScanOrderView.pack_id`. */
    pack_id: string | null;
    order_id: number;
    status: string;
    buyer_nickname: string | null;
    buyer_name: string;
    date_created: string;
  } | null;
  shipment: {
    ml_shipment_id: string | number;
    tracking_number: string | null;
    status: string | null;
    substatus: string | null;
  } | null;
  claim: { reason: string; detail: string; type: string; id: string } | null;
  already_processed: {
    wh_item_id: number;
    batch_id: number;
    item_status: string;
    closed_at: string | null;
  } | null;
  unlinked: boolean;
  seller: { id: number; nickname: string };
  cross_seller: boolean;
}
